import { getMonthText } from '../../../../../../../../Utils/dateUtils';
import {
  ANALYSKATEGORI_ID_VISA_SAMMANFATTNING,
  SpecifikationTypeEnum,
} from '../../../Const/EkonomiskAnalysConsts';
import { StandardColumnType } from '../../../Queries/EkonomiskAnalysBaseParamsQuery/EkonomiskAnalysBaseParamsQuery';
import { IColumn } from '../../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { ekanalys_ekanalys_columns } from '../../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import {
  BudgetTypes,
  ColumnFaltkodType,
} from '../../SideMenu/Components/PeriodOchKolumner/EditColumn/Components/EkonomiskAnalysEditColumnForm';
import {
  convertColumnTime,
  getReferenceDates,
} from '../../SideMenu/Components/PeriodOchKolumner/EditColumn/Utils/EkonomiskAnalysConvertColumnToEditColumnFormValues';
import { editColumnFormValuesToColumn } from '../../SideMenu/Components/PeriodOchKolumner/EditColumn/Utils/EkonomiskAnalysConvertEditColumnFormValuesToColumn';
import {
  findStandardColumnByFaltkod,
  getNewColumn,
  getStandardColumnSubTitle,
  getStandardColumnTitle,
} from '../../SideMenu/Components/PeriodOchKolumner/EditColumn/Utils/EkonomiskAnalysEditColumnUtils';
import { getNextColumnId } from '../../SideMenu/Components/PeriodOchKolumner/SelectColumn/Utils/EkonomiskAnalysColumnSelect.utils';
import {
  IEkonomiskAnalysDates,
  IEkonomiskAnalysFilledDates,
} from '../AnalysSettingsHandler';

const columnExists = (
  columnData: ekanalys_ekanalys_columns,
  columns: IColumn[]
): boolean => {
  return columns.some(
    column =>
      column.data.FALTKOD === columnData.FALTKOD &&
      column.data.FROM_AR === columnData.FROM_AR &&
      column.data.FROM_AR_RELATIV === columnData.FROM_AR_RELATIV &&
      column.data.FROM_PER === columnData.FROM_PER &&
      column.data.FROM_PER_RELATIV === columnData.FROM_PER_RELATIV &&
      column.data.TOM_AR === columnData.TOM_AR &&
      column.data.TOM_AR_RELATIV === columnData.TOM_AR_RELATIV &&
      column.data.TOM_PER === columnData.TOM_PER &&
      column.data.TOM_PER_RELATIV === columnData.TOM_PER_RELATIV
  );
};

const addCalculatedColumnDependency = (
  dependencyFaltkod: ColumnFaltkodType | undefined,
  standardColumns: StandardColumnType[] | undefined,
  column: IColumn,
  columns: IColumn[],
  newColumns: IColumn[],
  referenceDates: IEkonomiskAnalysDates
) => {
  if (dependencyFaltkod) {
    const columnDependency = findStandardColumnByFaltkod(
      dependencyFaltkod,
      standardColumns
    );
    if (columnDependency && standardColumns) {
      const id = getNextColumnId(newColumns);
      const fromArChecked = !column.data.FROM_AR_RELATIV;
      const tomArChecked = !column.data.TOM_AR_RELATIV;
      const fromPeriodChecked = !column.data.FROM_PER_RELATIV;
      const tomPeriodChecked = !column.data.TOM_PER_RELATIV;

      let { fromYear, fromMonth, toYear, toMonth } =
        getReferenceDates(referenceDates);

      const newColumnData = editColumnFormValuesToColumn(
        {
          columnOrigin: columnDependency.FALTKOD as ColumnFaltkodType,
          displayLabel: '',
          title: getStandardColumnTitle(columnDependency),
          subTitle: getStandardColumnSubTitle(
            columnDependency,
            fromPeriodChecked,
            tomPeriodChecked
          ),
          columnType: column.data.FALT_TYP,
          columnUnit: column.data.ENHET_ID,
          timeInterval: column.data.INTERVALL_TYP,
          fromAr: convertColumnTime(
            fromArChecked,
            fromYear,
            column.data.FROM_AR
          ),
          fromArChecked: fromArChecked,
          fromPeriod: convertColumnTime(
            fromPeriodChecked,
            fromMonth,
            column.data.FROM_PER
          ),
          fromPeriodChecked: fromPeriodChecked,
          tomAr: convertColumnTime(tomArChecked, toYear, column.data.TOM_AR),
          tomArChecked: tomArChecked,
          tomPeriod: convertColumnTime(
            tomPeriodChecked,
            toMonth,
            column.data.TOM_PER
          ),
          tomPeriodChecked: tomPeriodChecked,
          budget: column.data.BUDGETBENAMNING,
          budgetType: column.data.BUDGETTYP as BudgetTypes,
          flipSign: column.data.VAND_TECKEN || false,
          rullande: column.data.RULLANDE,
        },
        { ...getNewColumn(), HIDE_COLUMN: true, LOPNR: id },
        standardColumns,
        referenceDates
      );

      if (!columnExists(newColumnData, columns)) {
        newColumns.push({
          id: id,
          data: newColumnData,
          name: '',
        });
      }
    }
  }
};

export const addCalculatedColumnDependencies = (
  columns: IColumn[],
  standardColumns: StandardColumnType[] | undefined,
  referenceDates: IEkonomiskAnalysDates
): IColumn[] => {
  const newColumns = [...columns];
  columns.forEach(column => {
    const standardColumn = findStandardColumnByFaltkod(
      column.data.FALTKOD as ColumnFaltkodType,
      standardColumns
    );

    if (
      (!column.data.HIDE_COLUMN || column.data.FORCE_CALCULATE) &&
      standardColumn?.FIELDOPERATOR == '%'
    ) {
      addCalculatedColumnDependency(
        standardColumn?.ANVANDER1 as ColumnFaltkodType,
        standardColumns,
        column,
        columns,
        newColumns,
        referenceDates
      );
    }
    if (
      (!column.data.HIDE_COLUMN || column.data.FORCE_CALCULATE) &&
      standardColumn?.FIELDOPERATOR == '%'
    ) {
      addCalculatedColumnDependency(
        standardColumn?.ANVANDER2 as ColumnFaltkodType,
        standardColumns,
        column,
        columns,
        newColumns,
        referenceDates
      );
    }
  });

  return newColumns;
};

export const shouldCreateColumnsForUtgaendePrevPeriod = (
  analysCategoryID: number | undefined
): boolean => {
  if (analysCategoryID === ANALYSKATEGORI_ID_VISA_SAMMANFATTNING) {
    return true;
  } else {
    return false;
  }
};

const createIColumn = (
  prevUtgaendeColumn: IColumn,
  fromPer: number,
  toPer: number,
  month: number,
  specifikation?: string
): IColumn => {
  const newColumn: IColumn = {
    ...prevUtgaendeColumn,
    data: { ...prevUtgaendeColumn.data },
  };
  newColumn.name = `Utgående värde månad ${month}`;
  newColumn.data.BENAMNING2 = `Månad ${month}`;
  newColumn.data.FROM_PER = fromPer;
  newColumn.data.TOM_PER = toPer;

  //Göm kolumnen i EkA men skicka data med hjälp av FORCE_CALCULATE
  newColumn.data.HIDE_COLUMN = true;
  newColumn.data.FORCE_CALCULATE = true;
  if (specifikation) {
    newColumn.data.SPECIFIKATION = specifikation;
  }

  return newColumn;
};

export const createColumnsForPartYear = (
  periodDates: IEkonomiskAnalysFilledDates | null,
  prevUtgaendeColumn: IColumn | undefined,
  prevUtgaendeIntaktColumn: IColumn | undefined,
  prevUtgaendeKostnadColumn: IColumn | undefined
): IColumn[] => {
  const columnsForPrevPeriods: IColumn[] = [];
  if (!periodDates || !prevUtgaendeColumn) {
    return [];
  }
  //For prev year include all months up to December
  const endPeriod = periodDates.from.month;
  //Edge case med att inkludera intäkt och kostnad för nuvarande perioden
  if (prevUtgaendeIntaktColumn) {
    const newIntaktColumn = createIColumn(
      prevUtgaendeIntaktColumn,
      0,
      0,
      endPeriod,
      'I'
    );
    columnsForPrevPeriods.push(newIntaktColumn);
  }

  if (prevUtgaendeKostnadColumn) {
    const newKostnadColumn = createIColumn(
      prevUtgaendeKostnadColumn,
      0,
      0,
      endPeriod,
      'K'
    );
    columnsForPrevPeriods.push(newKostnadColumn);
  }

  if (endPeriod > 1) {
    for (let month = endPeriod - 1; month > 0; month--) {
      if (prevUtgaendeIntaktColumn) {
        const newIntaktColumn = createIColumn(
          prevUtgaendeIntaktColumn,
          month - endPeriod,
          month - endPeriod,
          month,
          'I'
        );
        columnsForPrevPeriods.push(newIntaktColumn);
      }

      if (prevUtgaendeKostnadColumn) {
        const newKostnadColumn = createIColumn(
          prevUtgaendeKostnadColumn,
          month - endPeriod,
          month - endPeriod,
          month,
          'K'
        );
        columnsForPrevPeriods.push(newKostnadColumn);
      }

      const newColumn = createIColumn(
        prevUtgaendeColumn,
        month - endPeriod,
        month - endPeriod,
        month
      );
      columnsForPrevPeriods.push(newColumn);
    }
  }

  return columnsForPrevPeriods;
};

//Skapa kolumner för alla månader i föregående år relativt utifrån vald period
export const createColumnsForFullPrevYear = (
  periodDates: IEkonomiskAnalysFilledDates | null,
  prevUtgaendeColumn: IColumn | undefined
): IColumn[] => {
  const columnsForPrevPeriods: IColumn[] = [];
  if (!periodDates || !prevUtgaendeColumn) {
    return [];
  }

  const firstMonth = 1;
  const lastMonth = 12;

  for (let month = firstMonth; month < lastMonth + 1; month++) {
    const newColumn: IColumn = {
      ...prevUtgaendeColumn,
      data: { ...prevUtgaendeColumn.data },
    };
    const monthText = getMonthText(month);

    newColumn.name = `Utgående värde månad ${monthText}`;
    newColumn.data.BENAMNING2 = `Månad ${monthText}`;
    newColumn.data.FROM_PER_RELATIV = false;
    newColumn.data.TOM_PER_RELATIV = false;
    newColumn.data.FROM_PER = month;
    //Om det är sista perioden i förgående år så vill vi ta med period 13
    newColumn.data.TOM_PER = month === 12 ? 13 : month;

    //Göm kolumnen i EkA men skicka data med hjälp av FORCE_CALCULATE
    newColumn.data.HIDE_COLUMN = true;
    newColumn.data.FORCE_CALCULATE = true;

    columnsForPrevPeriods.push(newColumn);
  }

  return columnsForPrevPeriods;
};

export const getUtgaendePeriodColumn = (
  columns: IColumn[],
  fromArRelativeToCurrentYear: number,
  type: SpecifikationTypeEnum | null
): IColumn | undefined => {
  return columns.find(
    column =>
      column.data.FALTKOD === 'PERUTGVARDE' &&
      column.data.FROM_AR === fromArRelativeToCurrentYear &&
      column.data.FROM_PER === 0 &&
      column.data.TOM_AR === 0 &&
      column.data.TOM_PER === 0 &&
      column.data.SPECIFIKATION === type
  );
};
